import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { useDataAuth } from '../../../hooks/useDataAuth';
import { useApiResolver } from '../../../hooks/useApiResolver';
import { useResolveCompanyInfo } from '../../../hooks/useResolveCompanyInfo';

import { PopUpSharedTable } from './PopUpSharedTable';
import { DialogConfirmAction } from '../../../components/dialogs/DialogConfirmAction';
import { KendoTreeLisEdit } from '../../../components/parsetEditor/KendoTreeLisEdit';
import { DetailsExecutionLog } from '../../../components/flows/DetailsExecutionLog';
import { DownloadAllFiles } from '../../../components/others/DownloadAllFiles';
import { GenericModal } from '../../../components/others/GenericModal';
import { Navigation } from '../../../components/others/Navigation';
import { ShowDocument } from '../../../components/sharedViews/loadActions/ShowDocument';
import { ModalForms } from '../../../components/sharedViews/loadActions/ModalForms';
import { safeParseJSON, safeStringifyJSON } from '../../../utils/jsonValidator';
import { ActionIconItem } from '../../../components/others/ActionIconItem';
import { evaluateCondition } from '../../../utils/evaluateCondition';
import { ensureArray } from '../../../utils/validateResponseApi';

export const LoadButtons = ({ data, handleChange, DocManager = {}, isMobileScreen = false, isCustomButton }) => {
    const { t } = useTranslation();
    const { resolveValue } = useApiResolver();
    const [{ userSession }] = useDataAuth();
    const companyInfo = useResolveCompanyInfo();

    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [tittleDiealog, setTittleDiealog] = useState('');
    const [flowProcess, setFlowProcess] = useState([]);
    const [trigger, setTrigger] = useState(null);
    const [idReg, setIdReg] = useState('');
    const [openLoadingOverlay, setopenLoadingOverlay] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [openModalJson, setOpenModalJson] = useState(false);
    const handleOpenModalJson = (rowData) => {
        setSelectedRowData(rowData.response);
        setOpenModalJson(true);
    };

    const [openModal, setOpenModal] = useState(null);
    const handleOpenModal = (item) => item && setOpenModal(item);
    const handleCloseModal = () => setOpenModal(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    useEffect(() => {
        setFlowProcess(
            data?.configuration
                ? JSON.parse(data?.configuration)?.flow_process
                : []
        );
    }, [data]);

    const CallApiTrigger = async () => {
        setopenLoadingOverlay(true);
        let bodyData = trigger[0]?.bodyData;
        const url = trigger[0]?.url;
        const method = trigger[0]?.method;

        let varb = safeParseJSON(bodyData?.variables);
        varb.id_company = companyInfo.id_company;
        varb.select_company = companyInfo.select_company;
        varb.id = idReg;
        bodyData.variables = safeStringifyJSON(varb, 0);

        const { success } = await resolveValue({
            method,
            url,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userSession.token}`,
            },
            data: JSON.stringify(bodyData),
        });

        if (success)
            setTimeout(() => handleChange(), 500);

        setOpenConfirmation(false);
        setopenLoadingOverlay(false);
    };

    const OpenConfirmation = (param, param2, param3) => {
        setOpenConfirmation(true);
        setTrigger(param);
        setIdReg(param2);
        setTittleDiealog(param3);
    };

    const getContent = useCallback((item, index) => {
        if (!item) return null;
        return (
            <>
                {item.type === 'showDocument' && <ShowDocument data={item} />}

                {item.type === 'trigger' && (
                    <ActionIconItem
                        item={item}
                        onClick={() => OpenConfirmation(item.trigger, data?.id, item.accion)}
                        defaultIcon="Task"
                    />
                )}

                {item.type === 'form' && <ModalForms data={item} dtoReg={data} handleChange={handleChange} companyInfo={companyInfo} />}

                {item.type === 'DownloadFile' && <DownloadAllFiles data={item} />}

                {item.type === 'Redirect' && <Navigation item={item} data={data} />}

                {item.type === 'detailsTable' && <PopUpSharedTable item={item} data={data} DocManager={DocManager} handleChange={() => item.refreshTable && handleChange()} />}

                {item.type === 'RedirectModal' && (
                    <>
                        <ActionIconItem
                            item={item}
                            onClick={() => handleOpenModal(item)}
                            defaultIcon="Task"
                        />
                        {openModal && (
                            <GenericModal
                                open={!!openModal}
                                title={openModal.accion}
                                handleClose={handleCloseModal}
                                item={item}
                                data={data}
                            />
                        )}
                    </>
                )}

                {item.type === 'formParcer' && <KendoTreeLisEdit item={item} data={data} />}

                {item.type === 'jsonVw' && (
                    <ActionIconItem
                        item={item}
                        onClick={() => handleOpenModalJson(data)}
                        defaultIcon="Task"
                    />
                )}
            </>
        );
    }, [data, handleChange, openModal]);

    return (
        <>
            {flowProcess && flowProcess.length > 0 && (() => {
                const visibleItems = ensureArray(flowProcess.filter((item) => !item?.visible || evaluateCondition(item.visible, data)));
                if ((isMobileScreen && visibleItems.length > 3) || isCustomButton) {
                    return (
                        <>
                            <IconButton
                                size="small"
                                sx={{ p: 0 }}
                                onClick={handleOpenMenu}
                            >
                                <MoreHorizIcon sx={{ color: "#6728AF" }} fontSize="medium" />
                            </IconButton>

                            <Menu
                                disableAutoFocusItem
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleCloseMenu}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{
                                    sx: {
                                        backgroundColor: "#6728AF",
                                        borderRadius: "10px",
                                        color: "#FFFFFF",
                                        mt: 1,
                                    },
                                }}
                            >
                                {visibleItems.map((item, idx) => {
                                    const content = getContent(item, idx);
                                    const onClick = content.props?.onClick;
                                    return (
                                        <MenuItem key={idx} onClick={onClick}>
                                            <span style={{ display: "flex", alignItems: "center", gap: 8, color: "#FFFFFF" }}>
                                                {content}
                                                {item.accion}
                                            </span>
                                        </MenuItem>
                                    );
                                })}
                            </Menu>
                        </>
                    );
                }

                return visibleItems.map((item, index) => getContent(item, index));
            })()}

            {
                openConfirmation &&
                <DialogConfirmAction
                    openDialogConfirm={openConfirmation}
                    openLoadingOverlay={openLoadingOverlay}
                    setOpenDialogConfirm={() => setOpenConfirmation(false)}
                    titleDialog={tittleDiealog}
                    textConfirmAction={t("confirmationPrompt")}
                    textButtonConfirm={t("accept")}
                    textButtonCancel={t("cancel")}
                    handleConfirmAction={CallApiTrigger}
                />
            }


            {openModalJson && (
                <DetailsExecutionLog
                    open={openModalJson}
                    data={selectedRowData}
                    setOpen={setOpenModalJson}
                />
            )}
        </>
    );
};
